"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useSyncExternalStore } from "react";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWater } from "@fortawesome/free-solid-svg-icons";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import HoverCard from "@/components/utils/tooltip/HoverCard";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { CurationWavePreviewCard } from "./CurationWavePreviewCard";
type CurationWaveBadgeSize = "default" | "compact";
interface CurationWaveBadgeProps {
  readonly waveId: string;
  readonly tooltipId?: string | undefined;
  readonly onBadgeClick?: (() => void) | undefined;
  readonly size?: CurationWaveBadgeSize | undefined;
  readonly profileIdentity?: string | null | undefined;
  readonly waveName?: string | null | undefined;
  readonly wavePfp?: string | null | undefined;
}
const BADGE_SIZE_STYLES: Record<CurationWaveBadgeSize, {
  readonly buttonClassName: string;
  readonly fallbackIconClassName: string;
  readonly imageSize: number;
}> = {
  default: {
    buttonClassName: "tw-h-5 tw-w-5",
    fallbackIconClassName: "tw-h-2.5 tw-w-2.5",
    imageSize: 20
  },
  compact: {
    buttonClassName: "tw-h-[18px] tw-w-[18px]",
    fallbackIconClassName: "tw-h-2 tw-w-2",
    imageSize: 18
  }
};
const getTrimmedText = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed.length === 0 ? null : trimmed;
};
const subscribeToClientRender = () => () => {};
const getClientRenderSnapshot = () => true;
const getServerRenderSnapshot = () => false;
export const CurationWaveBadge: React.FC<CurationWaveBadgeProps> = t0 => {
  const $ = _c(38);
  const {
    waveId,
    tooltipId: t1,
    onBadgeClick,
    size: t2,
    profileIdentity,
    waveName,
    wavePfp
  } = t0;
  const tooltipId = t1 === undefined ? "wave-creator-badge" : t1;
  const size = t2 === undefined ? "default" : t2;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const isClientHydrated = useSyncExternalStore(subscribeToClientRender, getClientRenderSnapshot, getServerRenderSnapshot);
  const [failedImageSrc, setFailedImageSrc] = useState(null);
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  let t3;
  if ($[0] !== waveName) {
    t3 = getTrimmedText(waveName);
    $[0] = waveName;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const normalizedWaveName = t3;
  let normalizedWavePfp;
  let t4;
  if ($[2] !== wavePfp) {
    normalizedWavePfp = getTrimmedText(wavePfp);
    t4 = normalizedWavePfp ? getScaledImageUri(normalizedWavePfp, ImageScale.W_AUTO_H_50) : null;
    $[2] = wavePfp;
    $[3] = normalizedWavePfp;
    $[4] = t4;
  } else {
    normalizedWavePfp = $[3];
    t4 = $[4];
  }
  const waveImageSrc = t4;
  const waveLabel = normalizedWaveName ?? "Featured wave";
  const ariaLabel = normalizedWaveName ? `Open ${normalizedWaveName}` : "Open featured wave";
  const sizeStyles = BADGE_SIZE_STYLES[size];
  const buttonClassName = `tw-inline-flex ${sizeStyles.buttonClassName} tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-white/15 tw-bg-white/5 tw-text-iron-200 tw-outline-none tw-ring-0 tw-transition-colors tw-duration-200 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/20 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-950 desktop-hover:hover:tw-border-white/25 desktop-hover:hover:tw-bg-white/10 desktop-hover:hover:tw-text-iron-100`;
  const showImage = waveImageSrc !== null && failedImageSrc !== waveImageSrc;
  const imageSrc = showImage ? waveImageSrc : null;
  const usesSheetPreview = isClientHydrated && hasTouchScreen;
  const t5 = usesSheetPreview ? "sheet" : "hovercard";
  let t6;
  if ($[5] !== normalizedWaveName || $[6] !== normalizedWavePfp || $[7] !== profileIdentity || $[8] !== t5 || $[9] !== waveId) {
    t6 = <CurationWavePreviewCard waveId={waveId} profileIdentity={profileIdentity} fallbackName={normalizedWaveName} fallbackPfp={normalizedWavePfp} variant={t5} />;
    $[5] = normalizedWaveName;
    $[6] = normalizedWavePfp;
    $[7] = profileIdentity;
    $[8] = t5;
    $[9] = waveId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const previewCard = t6;
  let t7;
  if ($[11] !== onBadgeClick || $[12] !== usesSheetPreview) {
    t7 = e => {
      e.preventDefault();
      e.stopPropagation();
      if (usesSheetPreview) {
        setIsMobilePreviewOpen(true);
        return;
      }
      onBadgeClick?.();
    };
    $[11] = onBadgeClick;
    $[12] = usesSheetPreview;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = usesSheetPreview ? "dialog" : undefined;
  const t9 = usesSheetPreview ? isMobilePreviewOpen : undefined;
  let t10;
  if ($[14] !== imageSrc || $[15] !== sizeStyles.fallbackIconClassName || $[16] !== sizeStyles.imageSize) {
    t10 = imageSrc ? <Image src={imageSrc} alt="" width={sizeStyles.imageSize} height={sizeStyles.imageSize} unoptimized={true} className="tw-block tw-object-cover" style={{
      width: `${sizeStyles.imageSize}px`,
      height: `${sizeStyles.imageSize}px`,
      objectFit: "cover"
    }} onError={() => setFailedImageSrc(imageSrc)} /> : <FontAwesomeIcon icon={faWater} className={`${sizeStyles.fallbackIconClassName} tw-flex-shrink-0 tw-text-current`} aria-hidden="true" />;
    $[14] = imageSrc;
    $[15] = sizeStyles.fallbackIconClassName;
    $[16] = sizeStyles.imageSize;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== ariaLabel || $[19] !== buttonClassName || $[20] !== t10 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9 || $[24] !== tooltipId || $[25] !== waveId) {
    t11 = <button type="button" onClick={t7} className={buttonClassName} aria-label={ariaLabel} aria-haspopup={t8} aria-expanded={t9} data-wave-id={waveId} data-tooltip-id={tooltipId}>{t10}</button>;
    $[18] = ariaLabel;
    $[19] = buttonClassName;
    $[20] = t10;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = tooltipId;
    $[25] = waveId;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const button = t11;
  if (usesSheetPreview) {
    let t12;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = () => setIsMobilePreviewOpen(false);
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== isMobilePreviewOpen || $[29] !== previewCard) {
      t13 = <MobileWrapperDialog isOpen={isMobilePreviewOpen} onClose={t12} noPadding={true} showScrollbar={true} zIndexClassName="tw-z-[1200]">{previewCard}</MobileWrapperDialog>;
      $[28] = isMobilePreviewOpen;
      $[29] = previewCard;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== button || $[32] !== t13) {
      t14 = <>{button}{t13}</>;
      $[31] = button;
      $[32] = t13;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    return t14;
  }
  const t12 = `Wave details for ${waveLabel}`;
  let t13;
  if ($[34] !== button || $[35] !== previewCard || $[36] !== t12) {
    t13 = <HoverCard content={previewCard} ariaLabel={t12} placement="auto" delayShow={500} delayHide={0} offset={10}>{button}</HoverCard>;
    $[34] = button;
    $[35] = previewCard;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
};